import { CSSProperties, ElementType, ReactNode, forwardRef, useRef } from 'react';

import styled from 'styled-components';

import { useIsOverflowing } from '@hooks/use-is-overflowing';
import { Color, Font, theme } from '@styles/theme';
import { XOR } from '@utils/types';

import { Tooltip } from '../tooltip';

type TextBaseProps = {
  font?: Font;
  tag?: ElementType;
  color?: Color;
  style?: CSSProperties;
  htmlFor?: string;
  caps?: boolean;
  capitalize?: boolean;
  className?: string;
  tooltip?: string;
  ellipsis?: boolean;
  underline?: boolean;
  aiGradient?: boolean;
  center?: boolean;
  dataTestid?: string;
} & XOR<{ text: ReactNode }, { children: ReactNode }>;

export type TextProps = TextBaseProps & { ellipsisWithTooltip?: boolean };

export const Text = forwardRef(function Text(props: TextProps, ref: React.Ref<HTMLElement>) {
  const { ellipsisWithTooltip = false } = props;

  const TextComponent = ellipsisWithTooltip ? TextWithEllipsedCheck : TextBase;

  return <TextComponent {...props} ref={ref} />;
});

const TextWithEllipsedCheck = forwardRef(function TextWithEllipsedCheck(
  props: TextBaseProps,
  ref: React.Ref<HTMLElement>
) {
  const localRef = useRef<HTMLElement>(null);
  const elementRef = (ref || localRef) as React.RefObject<HTMLElement>;
  const isTextEllipsed = useIsOverflowing(elementRef);

  const tooltip =
    props.tooltip ?? isTextEllipsed ? ((props.text ?? props.children) as string) : undefined;

  return <TextBase {...props} ref={elementRef} ellipsis tooltip={tooltip} />;
});

const TextBase = forwardRef(function TextBase(props: TextBaseProps, ref: React.Ref<HTMLElement>) {
  const {
    text,
    children,
    tag = 'span',
    font = 'regular16',
    color = 'textPrimary',
    caps = false,
    capitalize = false,
    ellipsis = false,
    style,
    tooltip,
    underline,
    aiGradient = false,
    center = false,
    dataTestid,
    ...rest
  } = props;

  const content = (
    <StyledText
      ref={ref}
      as={tag}
      $font={font}
      children={text ?? children}
      $color={color}
      $ellipsis={ellipsis}
      $underline={underline}
      $aiGradient={aiGradient}
      $center={center}
      {...rest}
      data-testid={dataTestid}
      style={{
        textTransform: capitalize ? 'capitalize' : caps ? 'uppercase' : 'none',
        ...style,
      }}
    />
  );

  return tooltip ? <Tooltip content={tooltip}>{content}</Tooltip> : content;
});

const StyledText = styled.span<{
  $font: Font;
  $color: Color;
  $ellipsis: boolean;
  $underline: boolean;
  $aiGradient?: boolean;
  $center?: boolean;
}>((p) => ({
  display: 'block',
  font: theme.font[p.$font],
  color: theme.color[p.$color],
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  textDecoration: p.$underline ? 'underline' : 'none',
  textAlign: p.$center ? 'center' : undefined,

  ...(p.$ellipsis && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  ...(p.$aiGradient && {
    background: 'linear-gradient(120deg, #005FFF 0%, #E039CE 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    width: 'fit-content',
  }),
}));

export const CustomContent = styled(Text).attrs({
  tag: 'p',
})({
  margin: 0,
  color: theme.custom.contentColor,
});

export const CustomHeading2 = styled(Text).attrs({
  tag: 'h2',
  font: 'heading1',
})({
  margin: 0,
  color: theme.custom.heading2Color,
});

export const CustomHeading3 = styled(Text).attrs({
  tag: 'h3',
  font: 'heading2',
})({
  margin: 0,
  color: theme.custom.heading3Color,
});

export const CustomHeading4 = styled(Text).attrs({
  tag: 'h4',
  font: 'bold16',
})({
  margin: 0,
  color: theme.custom.heading4Color,
});
